/**
 * Create Dialogs for Master Database
 * - Create Game Dialog
 * - Create Achievement Dialog
 * - Create DLC Dialog
 */
import { Loader2, Plus, Search, Gamepad2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";

// Helper to format YYYY-MM-DD to MM/DD/YYYY for display
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }
  return dateStr;
};

// Helper for auto-masking date input (MM/DD/YYYY)
const handleDateInputMask = (inputValue) => {
  let digits = inputValue.replace(/\D/g, "");
  digits = digits.slice(0, 8);
  
  let formatted = "";
  if (digits.length > 0) {
    formatted = digits.slice(0, 2);
  }
  if (digits.length > 2) {
    formatted += "/" + digits.slice(2, 4);
  }
  if (digits.length > 4) {
    formatted += "/" + digits.slice(4, 8);
  }
  
  return formatted;
};

// Region options (fallback when no admin-managed list is provided via props)
const DEFAULT_REGION_OPTIONS = [
  { value: "none", label: "No Region" },
  { value: "WW", label: "Worldwide (WW)" },
  { value: "NA", label: "North America (NA)" },
  { value: "EU", label: "Europe (EU)" },
  { value: "JP", label: "Japan (JP)" },
  { value: "AS", label: "Asia (AS)" },
  { value: "AU", label: "Australia (AU)" },
  { value: "KR", label: "Korea (KR)" },
  { value: "CN", label: "China (CN)" },
];

/**
 * Create Game Dialog
 */
export function CreateGameDialog({
  open,
  onOpenChange,
  newGame,
  onNewGameChange,
  onCreate,
  // Config data
  platforms,
  genres,
  contentTags,
  systemOptions,
  getAvailableContentTags,
  // Platform lookup handlers
  onXboxLookup,
  xboxLookupLoading,
  onSteamLookup,
  steamLookupLoading,
  onPsnLookup,
  psnLookupLoading,
  // Admin-managed region options (falls back to built-in list)
  regionOptions = DEFAULT_REGION_OPTIONS,
  // Possible existing MDB games with similar names (shown as a hint)
  similarMatches = [],
}) {
  const handleDateChange = (e) => {
    const formatted = handleDateInputMask(e.target.value);
    if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [month, day, year] = formatted.split('/');
      onNewGameChange({...newGame, releaseDate: `${year}-${month}-${day}`, _dateDisplay: formatted});
    } else {
      onNewGameChange({...newGame, releaseDate: "", _dateDisplay: formatted});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add Master Game</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2 -mr-2">
          {similarMatches && similarMatches.length > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3" data-testid="similar-games-hint">
              <p className="text-amber-300 text-xs font-semibold mb-1.5">
                {similarMatches.length} similar game{similarMatches.length > 1 ? "s" : ""} already in the database
              </p>
              <p className="text-amber-200/50 text-[11px] mb-2">If this is the same game, consider linking it as a version instead of creating a duplicate.</p>
              <ul className="space-y-1">
                {similarMatches.map((g) => (
                  <li key={g.id} className="text-[11px] text-white/70 truncate" data-testid={`similar-game-${g.id}`}>
                    • {g.name}
                    {g.platform ? <span className="text-white/30"> — {g.platform}{g.system ? ` · ${g.system}` : ""}</span> : null}
                    {g.releaseDate ? <span className="text-white/20"> ({String(g.releaseDate).slice(0, 4)})</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-2">
            <Label>Game Name *</Label>
            <Input
              value={newGame.name}
              onChange={(e) => onNewGameChange({...newGame, name: e.target.value})}
              placeholder="e.g., Halo Infinite"
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Publisher</Label>
              <Input
                value={newGame.publisher}
                onChange={(e) => onNewGameChange({...newGame, publisher: e.target.value})}
                placeholder="e.g., Microsoft"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Developer</Label>
              <Input
                value={newGame.developer}
                onChange={(e) => onNewGameChange({...newGame, developer: e.target.value})}
                placeholder="e.g., 343 Industries"
                className="bg-black/50 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Box Art</Label>
            <div className="flex items-start gap-4">
              <ImageUpload
                folder="gamepop/games"
                currentImageUrl={newGame.boxArtUrl}
                onImageUploaded={(url) => onNewGameChange({...newGame, boxArtUrl: url})}
                size="md"
              />
              <div className="flex-1 space-y-2">
                <Input
                  value={newGame.boxArtUrl}
                  onChange={(e) => onNewGameChange({...newGame, boxArtUrl: e.target.value})}
                  placeholder="Or paste URL here..."
                  className="bg-black/50 border-white/10"
                />
                <p className="text-xs text-white/40">Upload an image or paste a URL</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Release Date</Label>
              <Input
                type="text"
                value={newGame.releaseDate ? formatDateForDisplay(newGame.releaseDate) : newGame._dateDisplay || ""}
                onChange={handleDateChange}
                placeholder="MM/DD/YYYY"
                className="bg-black/50 border-white/10"
                maxLength={10}
              />
            </div>
            <div className="space-y-2">
              <Label>Completion Estimate</Label>
              <Input
                value={newGame.completionEstimate}
                onChange={(e) => onNewGameChange({...newGame, completionEstimate: e.target.value})}
                placeholder="e.g., 20-30 hours"
                className="bg-black/50 border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Genres</Label>
            <div className="flex flex-wrap gap-2 p-2 border border-white/10 rounded-lg bg-black/50 max-h-32 overflow-y-auto">
              {[...genres].sort((a, b) => a.name.localeCompare(b.name)).map((genre) => (
                <label 
                  key={genre.id} 
                  className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors ${
                    newGame.genres.includes(genre.slug) 
                      ? 'bg-purple-500/30 border border-purple-500/50' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <Checkbox
                    checked={newGame.genres.includes(genre.slug)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onNewGameChange({...newGame, genres: [...newGame.genres, genre.slug]});
                      } else {
                        onNewGameChange({...newGame, genres: newGame.genres.filter(g => g !== genre.slug)});
                      }
                    }}
                    className="border-white/30"
                  />
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: genre.color }}
                  />
                  <span className="text-xs text-white">{genre.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-2">
                {platforms.map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 px-2 py-1 rounded bg-white/5 cursor-pointer hover:bg-white/10">
                    <Checkbox
                      checked={newGame.platforms.includes(p.slug)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onNewGameChange({...newGame, platforms: [...newGame.platforms, p.slug]});
                        } else {
                          const platformSystems = systemOptions[p.slug]?.map(s => s.value) || [];
                          onNewGameChange({
                            ...newGame, 
                            platforms: newGame.platforms.filter(x => x !== p.slug),
                            systems: newGame.systems.filter(s => !platformSystems.includes(s)),
                            contentTags: newGame.contentTags.filter(tagId => {
                              const tag = contentTags.find(t => t.id === tagId);
                              if (!tag) return false;
                              const tagSystem = platforms.flatMap(pl => pl.systems || []).find(s => s.id === tag.systemId);
                              return tagSystem && !platformSystems.includes(tagSystem.slug);
                            })
                          });
                        }
                      }}
                    />
                    <span className="text-xs">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={newGame.region} onValueChange={(v) => onNewGameChange({...newGame, region: v})}>
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {regionOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Systems based on selected platforms */}
          {newGame.platforms.length > 0 && (
            <div className="space-y-2">
              <Label>Systems</Label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-black/30 rounded border border-white/10">
                {newGame.platforms.flatMap(platform => 
                  (systemOptions[platform] || []).map(sys => (
                    <label key={sys.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={newGame.systems.includes(sys.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onNewGameChange({...newGame, systems: [...newGame.systems, sys.value]});
                          } else {
                            onNewGameChange({...newGame, systems: newGame.systems.filter(x => x !== sys.value)});
                          }
                        }}
                      />
                      <span className="text-xs">{sys.label}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
          
          {/* Availability */}
          <div className="space-y-2">
            <Label>Availability</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={newGame.availableDigital}
                  onCheckedChange={(checked) => onNewGameChange({...newGame, availableDigital: checked})}
                />
                <span className="text-sm">Digital</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={newGame.availablePhysical}
                  onCheckedChange={(checked) => onNewGameChange({...newGame, availablePhysical: checked})}
                />
                <span className="text-sm">Physical</span>
              </label>
            </div>
          </div>
          
          {/* Content Tags based on selected systems */}
          {getAvailableContentTags(newGame.systems).length > 0 && (
            <div className="space-y-2">
              <Label>Content Tags</Label>
              <div className="flex flex-wrap gap-2 p-2 bg-black/30 rounded border border-white/10">
                {getAvailableContentTags(newGame.systems).map(tag => (
                  <label key={tag.id} className="flex items-center gap-2 px-2 py-1 rounded bg-pink-500/10 cursor-pointer hover:bg-pink-500/20">
                    <Checkbox
                      checked={newGame.contentTags.includes(tag.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onNewGameChange({...newGame, contentTags: [...newGame.contentTags, tag.id]});
                        } else {
                          onNewGameChange({...newGame, contentTags: newGame.contentTags.filter(x => x !== tag.id)});
                        }
                      }}
                    />
                    <span className="text-xs text-pink-400">{tag.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {/* Xbox Title ID */}
          {newGame.platforms.some(p => p.toLowerCase() === "xbox") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-green-400" />
                Xbox Title ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newGame.xboxTitleId}
                  onChange={(e) => onNewGameChange({...newGame, xboxTitleId: e.target.value})}
                  placeholder="e.g., 1480657291 or 5841098B"
                  className="bg-black/50 border-white/10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onXboxLookup('new')}
                  disabled={xboxLookupLoading || !newGame.name}
                  className="border-green-500/30 hover:bg-green-500/10 text-green-400 whitespace-nowrap"
                  data-testid="xbox-lookup-btn"
                >
                  {xboxLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">Decimal or Hex format (e.g., 1480657291 or 5841098B)</p>
            </div>
          )}
          
          {/* Xbox Bing ID */}
          {newGame.platforms.some(p => p.toLowerCase() === "xbox") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-green-400" />
                Xbox Bing ID (Xbox 360)
              </Label>
              <Input
                value={newGame.xboxBingId || ""}
                onChange={(e) => onNewGameChange({...newGame, xboxBingId: e.target.value})}
                placeholder="e.g., 66acd000-77fe-1000-9115-d8025841098b"
                className="bg-black/50 border-white/10"
              />
              <p className="text-xs text-white/40">GUID format for Xbox 360 games (from dbox.tools)</p>
            </div>
          )}
          
          {/* Steam App ID */}
          {newGame.platforms.some(p => p.toLowerCase() === "steam") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-blue-400" />
                Steam App ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newGame.steamAppId}
                  onChange={(e) => onNewGameChange({...newGame, steamAppId: e.target.value})}
                  placeholder="e.g., 730"
                  className="bg-black/50 border-white/10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onSteamLookup('new')}
                  disabled={steamLookupLoading || !newGame.name}
                  className="border-blue-500/30 hover:bg-blue-500/10 text-blue-400 whitespace-nowrap"
                  data-testid="steam-lookup-btn"
                >
                  {steamLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">Required for Steam achievement sync. Click Lookup to search Steam.</p>
            </div>
          )}
          
          {/* PlayStation NP ID */}
          {newGame.platforms.some(p => p.toLowerCase() === "playstation") && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-blue-500" />
                PlayStation NP ID
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newGame.npCommunicationId}
                  onChange={(e) => onNewGameChange({...newGame, npCommunicationId: e.target.value})}
                  placeholder="e.g., NPWR12345_00"
                  className="bg-black/50 border-white/10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onPsnLookup('new')}
                  disabled={psnLookupLoading || !newGame.name}
                  className="border-blue-600/30 hover:bg-blue-600/10 text-blue-500 whitespace-nowrap"
                  data-testid="psn-lookup-btn"
                >
                  {psnLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1" />
                      Lookup
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">Required for PlayStation trophy sync. Click Lookup to search your trophy list.</p>
            </div>
          )}
          
          <Button onClick={onCreate} className="w-full bg-purple-600 hover:bg-purple-500">
            <Plus className="w-4 h-4 mr-2" />
            Create Game
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Create Achievement Dialog (inline with trigger)
 */
export function CreateAchievementDialog({
  open,
  onOpenChange,
  newAchievement,
  onNewAchievementChange,
  onCreate,
  selectedGame,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-500 h-6 text-[10px] px-2" data-testid="add-achievement-btn">
          <Plus className="w-3 h-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add Achievement</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2 -mr-2">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input
              value={newAchievement.name}
              onChange={(e) => onNewAchievementChange({...newAchievement, name: e.target.value})}
              placeholder="Achievement name"
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={newAchievement.description}
              onChange={(e) => onNewAchievementChange({...newAchievement, description: e.target.value})}
              placeholder="How to unlock..."
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Score (GS)</Label>
              <Input
                type="number"
                value={newAchievement.score}
                onChange={(e) => onNewAchievementChange({...newAchievement, score: parseInt(e.target.value) || 0})}
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Trophy Type (PS)</Label>
              <Select 
                value={newAchievement.trophyType || "none"} 
                onValueChange={(v) => onNewAchievementChange({...newAchievement, trophyType: v === "none" ? null : v})}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Achievement Image</Label>
            <div className="flex items-start gap-3">
              <ImageUpload
                folder="gamepop/achievements"
                currentImageUrl={newAchievement.imageUrl}
                onImageUploaded={(url) => onNewAchievementChange({...newAchievement, imageUrl: url})}
                size="sm"
              />
              <Input
                value={newAchievement.imageUrl}
                onChange={(e) => onNewAchievementChange({...newAchievement, imageUrl: e.target.value})}
                placeholder="Or paste URL..."
                className="bg-black/50 border-white/10 flex-1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>DLC (leave empty for base game)</Label>
            <Select 
              value={newAchievement.dlcId || "none"} 
              onValueChange={(v) => onNewAchievementChange({...newAchievement, dlcId: v === "none" ? null : v})}
            >
              <SelectTrigger className="bg-black/50 border-white/10">
                <SelectValue placeholder="Base Game" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="none">Base Game</SelectItem>
                {selectedGame?.dlcs?.map((dlc) => (
                  <SelectItem key={dlc.id} value={dlc.id}>{dlc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-start gap-2 px-2 py-2 rounded bg-black/30 border border-white/10 cursor-pointer hover:border-amber-400/30">
              <input
                type="checkbox"
                checked={!!newAchievement.isSecret}
                onChange={(e) => onNewAchievementChange({ ...newAchievement, isSecret: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-amber-400"
                data-testid="create-ach-secret"
              />
              <div className="text-xs">
                <div className="text-white/90 font-medium">Secret</div>
                <div className="text-white/40">Hide details until unlocked</div>
              </div>
            </label>
            <label className="flex items-start gap-2 px-2 py-2 rounded bg-black/30 border border-white/10 cursor-pointer hover:border-pink-400/30">
              <input
                type="checkbox"
                checked={!!newAchievement.isPerCharacter}
                onChange={(e) => onNewAchievementChange({ ...newAchievement, isPerCharacter: e.target.checked })}
                className="mt-0.5 w-4 h-4"
                style={{ accentColor: "#e11d74" }}
                data-testid="create-ach-per-character"
              />
              <div className="text-xs">
                <div className="text-white/90 font-medium">Per Character</div>
                <div className="text-white/40">Users can log unlocks per character</div>
              </div>
            </label>
          </div>
          <Button onClick={onCreate} className="w-full bg-purple-600 hover:bg-purple-500">
            <Plus className="w-4 h-4 mr-2" />
            Create Achievement
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Create DLC Dialog (inline with trigger)
 */
export function CreateDlcDialog({
  open,
  onOpenChange,
  newDlc,
  onNewDlcChange,
  onCreate,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-7" data-testid="add-dlc-btn">
          <Plus className="w-3 h-3 mr-1" />
          Add DLC
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Add DLC</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>DLC Name *</Label>
            <Input
              value={newDlc.name}
              onChange={(e) => onNewDlcChange({ ...newDlc, name: e.target.value })}
              className="bg-black/50 border-white/10"
              placeholder="Enter DLC name"
            />
          </div>
          <div className="space-y-2">
            <Label>Release Date</Label>
            <Input
              type="date"
              value={newDlc.releaseDate}
              onChange={(e) => onNewDlcChange({ ...newDlc, releaseDate: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Completion Estimate</Label>
            <Input
              value={newDlc.completionEstimate}
              onChange={(e) => onNewDlcChange({ ...newDlc, completionEstimate: e.target.value })}
              className="bg-black/50 border-white/10"
              placeholder="e.g., 2-3 hours"
            />
          </div>
          <div className="space-y-2">
            <Label>Region (optional)</Label>
            <Input
              value={newDlc.region || ""}
              onChange={(e) => onNewDlcChange({ ...newDlc, region: e.target.value })}
              className="bg-black/50 border-white/10"
              placeholder="e.g., JP, EU, US"
            />
          </div>
          <div className="space-y-2">
            <Label>Box Art</Label>
            <ImageUpload
              value={newDlc.boxArtUrl}
              onChange={(url) => onNewDlcChange({ ...newDlc, boxArtUrl: url })}
              folder="gamepop/dlcs"
            />
          </div>
          <Button 
            onClick={onCreate} 
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={!newDlc.name}
          >
            Add DLC
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
